/**
 * @file apps/web/src/pages/SettingsPage.tsx
 * @description Workspace settings hub — reachable from the sidebar gear
 * icon. Workspace section (name/logo/timezone) is visible to anyone who
 * can reach this page; Approval Settings is SI-Admin (tenant owner) only.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Users } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { tenantsApi, workspacesApi } from '../lib/api.js';

interface Tenant {
  id:   string;
  name: string;
}

interface WorkspaceSettings {
  timezone:                          string;
  uat_signoff_granularity:           string;
  require_fdd_approval_before_uat:   boolean;
  require_uat_signoff_before_launch: boolean;
}

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Sydney',
];

export function SettingsPage() {
  const { user, devHeaders } = useAuth();
  const isOwner = user.tenantRole === 'owner';

  const [name, setName]           = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [timezone, setTimezone]   = useState('UTC');
  const [settings, setSettings]   = useState<WorkspaceSettings | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [saved, setSaved]         = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      // Tenant name is already available via useAuth's tenantId — but the
      // display name itself isn't carried on AuthUser, so fetch it fresh.
      tenantsApi.get(user.tenantId, devHeaders) as Promise<Tenant>,
      workspacesApi.getSettings(user.tenantId, devHeaders) as Promise<WorkspaceSettings>,
    ])
      .then(([tenant, s]) => {
        if (cancelled) return;
        setName(tenant.name);
        setSettings(s);
        setTimezone(s.timezone);
        setError('');
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load settings'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveName = async () => {
    setNameSaving(true);
    try {
      await tenantsApi.update(user.tenantId, { name }, devHeaders);
      flashSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save workspace name');
    } finally {
      setNameSaving(false);
    }
  };

  const handleSaveTimezone = async (tz: string) => {
    setTimezone(tz);
    try {
      await workspacesApi.updateSettings(user.tenantId, { timezone: tz }, devHeaders);
      flashSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save timezone');
    }
  };

  const handleUpdateApprovalSetting = async (patch: Record<string, unknown>) => {
    if (!settings) return;
    setSettings({ ...settings, ...patch } as WorkspaceSettings);
    try {
      await workspacesApi.updateSettings(user.tenantId, patch, devHeaders);
      flashSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save approval setting');
    }
  };

  let savedTimer: ReturnType<typeof setTimeout> | null = null;
  const flashSaved = () => {
    setSaved(true);
    if (savedTimer) clearTimeout(savedTimer);
    savedTimer = setTimeout(() => setSaved(false), 1500);
  };

  if (loading) {
    return (
      <div className="p-8 max-w-3xl space-y-4">
        <div className="card h-16 animate-pulse bg-gray/60" />
        <div className="card h-48 animate-pulse bg-gray/60" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="text-sm text-muted mt-1">Workspace configuration</p>
        </div>
        <Link to="/settings/users" className="flex items-center gap-1.5 text-sm text-blue hover:underline">
          <Users size={14} /> Team Members
        </Link>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {saved && (
        <div className="p-2 mb-4 rounded-lg bg-success/8 text-success text-xs font-mono text-center">
          Saved
        </div>
      )}

      <div className="card p-5 space-y-4 mb-6">
        <h2 className="font-display text-sm font-semibold text-ink">Workspace Settings</h2>

        <div>
          <label className="label mb-1.5">Workspace Name</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input text-sm flex-1"
            />
            <button onClick={() => void handleSaveName()} disabled={nameSaving} className="btn-secondary text-sm">
              {nameSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div>
          <label className="label mb-1.5">Logo</label>
          <div className="flex items-center gap-3">
            <input type="file" accept="image/*" disabled className="input text-sm flex-1 opacity-50 cursor-not-allowed" />
          </div>
          <p className="text-[11px] font-mono text-muted mt-1">
            Logo upload — requires object storage integration, coming soon.
          </p>
        </div>

        <div>
          <label className="label mb-1.5">Timezone</label>
          <select
            value={timezone}
            onChange={(e) => void handleSaveTimezone(e.target.value)}
            className="select text-sm"
          >
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
      </div>

      {isOwner && settings && (
        <div className="card p-5 space-y-4">
          <div>
            <h2 className="font-display text-sm font-semibold text-ink">Approval Settings</h2>
            <p className="text-xs text-muted mt-0.5">SI Admin only</p>
          </div>

          <div>
            <label className="label mb-1.5">UAT Sign-off Granularity</label>
            <div className="flex gap-2">
              {(['per_cycle', 'per_subcycle'] as const).map((g) => (
                <button
                  key={g}
                  onClick={() => void handleUpdateApprovalSetting({ uatSignoffGranularity: g })}
                  className={[
                    'text-sm px-3 py-1.5 rounded-lg border transition-colors',
                    settings.uat_signoff_granularity === g
                      ? 'border-blue bg-sky text-blue'
                      : 'border-[rgba(8,17,31,0.12)] text-muted hover:text-ink',
                  ].join(' ')}
                >
                  {g === 'per_cycle' ? 'Per UAT Cycle' : 'Per Subcycle'}
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm text-ink">Require FDD approval before UAT can begin</span>
            <input
              type="checkbox"
              checked={settings.require_fdd_approval_before_uat}
              onChange={(e) => void handleUpdateApprovalSetting({ requireFddApprovalBeforeUat: e.target.checked })}
              className="rounded border-gray-300 text-blue focus:ring-blue"
            />
          </label>

          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm text-ink">Require UAT sign-off before Launch</span>
            <input
              type="checkbox"
              checked={settings.require_uat_signoff_before_launch}
              onChange={(e) => void handleUpdateApprovalSetting({ requireUatSignoffBeforeLaunch: e.target.checked })}
              className="rounded border-gray-300 text-blue focus:ring-blue"
            />
          </label>
        </div>
      )}
    </div>
  );
}
